// SkipJobCreatePage — /skip/jobs/create.
//
// Form-driven create. Customer search reuses the bookings CustomerSearch
// component. Site selector populates from useCustomerSites once a
// customer is chosen. Skip-size dropdown is from useSkipSizes (§7.5).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { CustomerSearch } from '../../bookings/components/CustomerSearch';
import {
  useCreateSkipJob,
  useSkipSizes,
} from '../hooks/useSkipJobs';
import { useActivatedLegacySkipTypes } from '../hooks/useActivatedLegacySkipTypes';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import { useSkipInventory } from '../hooks/useSkipInventory';
import { useDriversList } from '../../admin/drivers/hooks/useDriversAdmin';
import { SitePicker, type SitePickerValue } from '../../../components/ui/SitePicker/SitePicker';
import type { CustomerSummary } from '../../../types/api';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { DuplicateCustomerWarning } from '../../../components/ui/DuplicateCustomerWarning/DuplicateCustomerWarning';
import { useCheckCustomerDuplicates } from '../hooks/useSkipCustomers';
import styles from './SkipJobCreatePage.module.css';

const LEGACY_LABELS: Readonly<Record<string, string>> = {
  skip: 'Skip',
  bin: 'Bin',
  roro: 'Roro',
};

interface FormState {
  customer_uuid: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_phone2: string | null;
  customer_postcode: string;
  customer_address_line1: string;
  customer_address_line2: string;
  customer_city: string;
  customer_county: string;
  site_id: number | null;
  site_enc_id: string | null;
  job_address_line1: string | null;
  job_address_line2: string | null;
  job_address_city: string | null;
  job_address_postcode: string | null;
  skip_type: 'skip' | 'bin' | 'roro' | 'other';
  /** Bug 2 — enc_id of a custom (non-legacy) stock type, when one is
   *  chosen. skip_type stays 'other' on the persisted row. */
  custom_stock_type_uuid: string | null;
  /** Encrypted enc_id of the chosen skip_stock_sizes row. The
   *  /reference/skip-sizes catalogue returns enc_ids (not numeric
   *  ids), so the form binds the string directly — Number(enc_id)
   *  produces NaN and silently drops the selection. */
  skip_size_enc_id: string | null;
  /** Legacy ENUM key — sent for dual-write compat. */
  job_type: string;
  /** UUID of the chosen skip_job_types row — sent as
   *  skip_job_type_uuid in the payload to populate the FK. */
  skip_job_type_uuid: string | null;
  /** Optional inventory unit assigned at creation. */
  skip_inventory_uuid: string | null;
  /** Feature 1 — assigned driver. uuid of a drivers row. Optional;
   *  null leaves driver_id NULL on the new job. */
  driver_uuid: string | null;
  is_priority: boolean;
  priority_notes: string;
  delivery_date: string;
  delivery_time: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  customer_uuid: '',
  customer_name: '',
  customer_email: null,
  customer_phone: '',
  customer_phone2: null,
  customer_postcode: '',
  customer_address_line1: '',
  customer_address_line2: '',
  customer_city: '',
  customer_county: '',
  site_id: null,
  site_enc_id: null,
  job_address_line1: null,
  job_address_line2: null,
  job_address_city: null,
  job_address_postcode: null,
  skip_type: 'skip',
  custom_stock_type_uuid: null,
  skip_size_enc_id: null,
  job_type: 'delivery_only',
  skip_job_type_uuid: null,
  skip_inventory_uuid: null,
  driver_uuid: null,
  is_priority: false,
  priority_notes: '',
  delivery_date: '',
  delivery_time: '',
  notes: '',
};

export function SkipJobCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<CustomerSummary[]>([]);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [userChoseCreateAnyway, setUserChoseCreateAnyway] = useState(false);
  const checkDuplicates = useCheckCustomerDuplicates();
  const formRef = useRef<HTMLFormElement>(null);

  const createJob = useCreateSkipJob();
  const sizesQuery = useSkipSizes();
  const activatedLegacy = useActivatedLegacySkipTypes();
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  // Bug 4: only fetch inventory once a stock size has been picked. The
  // server-side skip_size_enc_id filter (added in this fix) returns
  // just the matching units, so the dropdown only shows valid options.
  const inventoryQuery = useSkipInventory(
    form.skip_size_enc_id !== null
      ? { status: 'available', limit: 100, skip_size_enc_id: form.skip_size_enc_id }
      : { status: 'available', limit: 100 },
  );
  const customerEnc = (form as FormState & { customer_enc?: string }).customer_enc ?? null;

  function handleSitePickerChange(value: SitePickerValue): void {
    if (value.type === 'site') {
      setForm((prev) => ({
        ...prev,
        site_enc_id: value.site_enc_id,
        job_address_line1: null,
        job_address_line2: null,
        job_address_city: null,
        job_address_postcode: null,
      }));
    } else if (value.type === 'one_off') {
      setForm((prev) => ({
        ...prev,
        site_enc_id: null,
        job_address_line1: value.line1,
        job_address_line2: value.line2,
        job_address_city: value.city,
        job_address_postcode: value.postcode,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        site_enc_id: null,
        job_address_line1: null,
        job_address_line2: null,
        job_address_city: null,
        job_address_postcode: null,
      }));
    }
  }

  async function handleCustomerBlur(): Promise<void> {
    if (!newCustomerMode || !form.customer_name.trim()) return;
    try {
      const result = await checkDuplicates.mutateAsync({
        name:     form.customer_name.trim(),
        phone:    form.customer_phone.trim() || null,
        postcode: form.customer_postcode.trim() || null,
      });
      setDuplicateMatches(result.matches);
    } catch {
      // non-fatal
    }
  }

  const jobTypes = jobTypesQuery.data?.jobTypes ?? [];
  const selectedJobType = jobTypes.find((t) => t.uuid === form.skip_job_type_uuid) ?? null;
  const isImmediate = selectedJobType?.process_immediately === true;

  // Default job type once data lands: pick the first non-immediate
  // option (delivery_only is the conventional first).
  useEffect(() => {
    if (form.skip_job_type_uuid !== null) return;
    if (jobTypes.length === 0) return;
    const def = jobTypes.find((t) => !t.process_immediately) ?? jobTypes[0]!;
    setForm((prev) => ({
      ...prev,
      skip_job_type_uuid: def.uuid,
      job_type: def.key,
    }));
  }, [jobTypes, form.skip_job_type_uuid]);

  // Inventory list is now server-filtered by skip_size_id (Bug 4) so
  // we just take whatever the query returned. When no size is picked
  // yet the picker is disabled (see render below); this memo's only
  // job is to surface a stable reference for the option list.
  const inventoryOptions = useMemo(
    () => inventoryQuery.data?.inventory ?? [],
    [inventoryQuery.data],
  );

  // Skip Type options: activated globals + own-tenant customs + the
  // generic 'other' catch-all. 'other' is not tied to any global so
  // it always appears. Bug 2 — customs encode their enc_id in the
  // option value as `custom:${enc_id}`.
  const typeOptions = useMemo(
    () => [
      ...activatedLegacy.types.map((t) => ({ value: t as string, label: LEGACY_LABELS[t] ?? t })),
      ...activatedLegacy.customTypes.map((c) => ({
        value: `custom:${c.uuid}`,
        label: c.name,
      })),
      { value: 'other', label: 'Other' },
    ],
    [activatedLegacy.types, activatedLegacy.customTypes],
  );

  // Feature 1 — drivers list for the Assign Driver dropdown.
  const driversQuery = useDriversList({ page: 1, limit: 100, status: 'active' });
  const drivers = driversQuery.data?.drivers ?? [];

  // Default skip_type tracks the first activated legacy type. Once the
  // hook resolves, switch the default if the current selection is not
  // a valid option (e.g. 'skip' is deactivated). 'other' is always
  // valid so we never override an explicit 'other' choice.
  useEffect(() => {
    if (activatedLegacy.isLoading) return;
    setForm((prev) => {
      if (prev.skip_type === 'other') return prev;
      if (activatedLegacy.types.includes(prev.skip_type as 'skip' | 'bin' | 'roro')) return prev;
      const next = activatedLegacy.types[0] ?? 'other';
      if (prev.skip_type === next) return prev;
      return { ...prev, skip_type: next, skip_size_enc_id: null };
    });
  }, [activatedLegacy.isLoading, activatedLegacy.types]);

  function handleCustomerSelect(c: CustomerSummary) {
    const enc = (c as CustomerSummary & { enc_id?: string }).enc_id ?? c.uuid;
    setForm((prev) => ({
      ...prev,
      customer_uuid: c.uuid,
      customer_name: c.name,
      customer_email: c.email ?? null,
      customer_phone: c.phone,
      customer_phone2: c.phone2 ?? null,
      customer_postcode: c.postcode ?? '',
      customer_address_line1: '',
      customer_address_line2: '',
      customer_city: '',
      customer_county: '',
      site_id: null,
      ...({ customer_enc: enc } as Partial<FormState>),
    }));
    setNewCustomerMode(false);
    setDuplicateMatches([]);
    setUserChoseCreateAnyway(false);
    setShowDuplicateConfirm(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.customer_uuid && !newCustomerMode) {
      setError('Please choose a customer or click "New customer".');
      return;
    }
    if (!form.customer_name || !form.customer_phone || !form.customer_postcode) {
      setError('Customer name, phone and postcode are required.');
      return;
    }
    if (newCustomerMode && duplicateMatches.length > 0 && !userChoseCreateAnyway) {
      setShowDuplicateConfirm(true);
      return;
    }
    const body: Record<string, unknown> = {
      ...(form.customer_uuid ? { customer_uuid: form.customer_uuid } : {}),
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone,
      customer_phone2: form.customer_phone2,
      customer_postcode: form.customer_postcode,
      skip_type: form.skip_type,
      job_type: form.job_type,
      // process_immediately types skip scheduling.
      delivery_date: isImmediate ? null : form.delivery_date || null,
      delivery_time: isImmediate ? null : form.delivery_time || null,
      notes: form.notes || null,
      is_priority: form.is_priority,
    };
    if (form.skip_size_enc_id !== null) body['skip_size_enc_id'] = form.skip_size_enc_id;
    if (form.site_enc_id !== null) {
      body['site_enc_id'] = form.site_enc_id;
    } else if (form.job_address_line1 && form.job_address_postcode) {
      body['site_enc_id'] = null;
      body['job_address_line1'] = form.job_address_line1;
      body['job_address_line2'] = form.job_address_line2;
      body['job_city'] = form.job_address_city;
      body['job_postcode'] = form.job_address_postcode;
      body['use_different_job_address'] = true;
    }
    if (form.skip_job_type_uuid !== null) {
      body['skip_job_type_uuid'] = form.skip_job_type_uuid;
    }
    if (form.skip_inventory_uuid !== null) {
      body['skip_inventory_uuid'] = form.skip_inventory_uuid;
    }
    if (form.driver_uuid !== null) {
      body['driver_uuid'] = form.driver_uuid;
    }
    if (form.is_priority && form.priority_notes.trim()) {
      body['priority_notes'] = form.priority_notes.trim();
    }
    if (newCustomerMode) {
      if (form.customer_address_line1.trim()) body['customer_address_line1'] = form.customer_address_line1.trim();
      if (form.customer_address_line2.trim()) body['customer_address_line2'] = form.customer_address_line2.trim();
      if (form.customer_city.trim()) body['customer_city'] = form.customer_city.trim();
      if (form.customer_county.trim()) body['customer_county'] = form.customer_county.trim();
    }

    createJob.mutate(body, {
      onSuccess: (data) => {
        navigate(`/skip/jobs/${encodeURIComponent(data.job.enc_id)}`);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Job"
        subtitle="Create a new stock job"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/jobs')}
          >
            Back to Jobs
          </Button>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
        <Card>
          <h2 className={styles.sectionHeading}>Customer Details</h2>
          <CustomerSearch
            newCustomerMode={newCustomerMode}
            onSelect={handleCustomerSelect}
            onToggleNewCustomer={(nm) => {
              setNewCustomerMode(nm);
              if (nm) {
                setDuplicateMatches([]);
                setUserChoseCreateAnyway(false);
                setShowDuplicateConfirm(false);
                setForm((prev) => ({
                  ...EMPTY_FORM,
                  skip_type: prev.skip_type,
                  job_type: prev.job_type,
                }));
              }
            }}
          />

          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Name</span>
              <input
                required
                value={form.customer_name ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                onBlur={() => { void handleCustomerBlur(); }}
              />
            </label>
            <label className={styles.field}>
              <span>Phone</span>
              <input
                required
                value={form.customer_phone ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_phone: e.target.value }))}
                onBlur={() => { void handleCustomerBlur(); }}
              />
            </label>
            <label className={styles.field}>
              <span>Email</span>
              <input
                type="email"
                value={form.customer_email ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, customer_email: e.target.value || null }))
                }
              />
            </label>
            <label className={styles.field}>
              <span>Postcode</span>
              <input
                required
                value={form.customer_postcode ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_postcode: e.target.value }))}
              />
            </label>
            {newCustomerMode && (
              <>
                <label className={styles.field}>
                  <span>Address Line 1</span>
                  <input
                    value={form.customer_address_line1}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_address_line1: e.target.value }))}
                    placeholder="Street address"
                  />
                </label>
                <label className={styles.field}>
                  <span>Address Line 2</span>
                  <input
                    value={form.customer_address_line2}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_address_line2: e.target.value }))}
                    placeholder="Apartment, suite, etc."
                  />
                </label>
                <label className={styles.field}>
                  <span>City</span>
                  <input
                    value={form.customer_city}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_city: e.target.value }))}
                    placeholder="City"
                  />
                </label>
                <label className={styles.field}>
                  <span>County</span>
                  <input
                    value={form.customer_county}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_county: e.target.value }))}
                    placeholder="County"
                  />
                </label>
              </>
            )}
          </div>
        </Card>

        {newCustomerMode && duplicateMatches.length > 0 && (
          <DuplicateCustomerWarning
            matches={duplicateMatches}
            showConfirm={showDuplicateConfirm}
            onUseExisting={(c) => {
              handleCustomerSelect(c);
              setDuplicateMatches([]);
              setUserChoseCreateAnyway(false);
            }}
            onCreateAnyway={() => {
              setUserChoseCreateAnyway(true);
              setDuplicateMatches([]);
            }}
            onDismiss={() => setDuplicateMatches([])}
            onConfirmCreate={() => {
              setUserChoseCreateAnyway(true);
              setShowDuplicateConfirm(false);
              formRef.current?.requestSubmit();
            }}
            onCancelConfirm={() => setShowDuplicateConfirm(false)}
          />
        )}

        <Card>
          <h2 className={styles.sectionHeading}>Job Site</h2>
          <SitePicker
            customerEncId={customerEnc}
            onChange={handleSitePickerChange}
          />
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Stock Details</h2>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Stock Type</span>
              <select
                value={
                  form.custom_stock_type_uuid !== null
                    ? `custom:${form.custom_stock_type_uuid}`
                    : form.skip_type
                }
                onChange={(e) => {
                  // Bug 3: only reset size + inventory when the type
                  // ACTUALLY changes. The previous unconditional reset
                  // also fired on no-op selections / refetches and
                  // nuked the user's chosen size unexpectedly.
                  const v = e.target.value;
                  if (v.startsWith('custom:')) {
                    const uuid = v.slice('custom:'.length);
                    setForm((prev) => {
                      if (prev.custom_stock_type_uuid === uuid) return prev;
                      return {
                        ...prev,
                        skip_type: 'other',
                        custom_stock_type_uuid: uuid,
                        skip_size_enc_id: null,
                        skip_inventory_uuid: null,
                      };
                    });
                  } else {
                    const next = v as FormState['skip_type'];
                    setForm((prev) => {
                      if (next === prev.skip_type && prev.custom_stock_type_uuid === null) return prev;
                      return {
                        ...prev,
                        skip_type: next,
                        custom_stock_type_uuid: null,
                        skip_size_enc_id: null,
                        skip_inventory_uuid: null,
                      };
                    });
                  }
                }}
              >
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Stock Size</span>
              <select
                value={form.skip_size_enc_id ?? ''}
                onChange={(e) => {
                  const nextSize = e.target.value === '' ? null : e.target.value;
                  // Bug 3 + 4: changing size resets the inventory pick
                  // because the unit list is now size-scoped server-side.
                  setForm((prev) => ({
                    ...prev,
                    skip_size_enc_id: nextSize,
                    skip_inventory_uuid:
                      nextSize === prev.skip_size_enc_id
                        ? prev.skip_inventory_uuid
                        : null,
                  }));
                }}
              >
                <option value="">— Select size —</option>
                {(sizesQuery.data?.skipSizes ?? [])
                  .filter((s) =>
                    form.custom_stock_type_uuid !== null
                      ? s.stock_type_uuid === form.custom_stock_type_uuid
                      : s.skip_type === form.skip_type,
                  )
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Job Type</span>
              <select
                value={form.skip_job_type_uuid ?? ''}
                onChange={(e) => {
                  const uuid = e.target.value || null;
                  const found = jobTypes.find((t) => t.uuid === uuid);
                  setForm((prev) => ({
                    ...prev,
                    skip_job_type_uuid: uuid,
                    // Dual-write: keep the legacy ENUM key in sync.
                    job_type: found?.key ?? prev.job_type,
                  }));
                }}
              >
                {jobTypes.length === 0 ? (
                  <option value="">— Loading types —</option>
                ) : null}
                {jobTypes.map((t) => (
                  <option key={t.uuid} value={t.uuid}>
                    {t.name}
                    {t.process_immediately ? ' (Immediate)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {isImmediate ? (
            <p className={styles.immediateBanner}>
              This job type will be marked as completed immediately on save.
              No scheduling is required.
            </p>
          ) : null}
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Assign Stock Unit</h2>
          <select
            value={form.skip_inventory_uuid ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, skip_inventory_uuid: e.target.value || null }))
            }
            className={styles.select}
            disabled={form.skip_size_enc_id === null}
          >
            <option value="">
              {form.skip_size_enc_id === null
                ? '— Select stock type and size first —'
                : '— No unit assigned —'}
            </option>
            {inventoryOptions.map((u) => (
              <option key={u.enc_id} value={u.uuid}>
                {u.skip_number}
                {u.stock_type_name ? ` — ${u.stock_type_name}` : ''}
                {u.size_name ? ` ${u.size_name}` : ''}
              </option>
            ))}
          </select>
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Priority</h2>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={form.is_priority}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  is_priority: e.target.checked,
                  priority_notes: e.target.checked ? prev.priority_notes : '',
                }))
              }
            />
            <span>Mark as Priority</span>
          </label>
          {form.is_priority ? (
            <label className={styles.field}>
              <span>Priority Notes</span>
              <textarea
                rows={2}
                value={form.priority_notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, priority_notes: e.target.value }))
                }
                placeholder="Why is this priority?"
                maxLength={5000}
              />
            </label>
          ) : null}
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Assign Driver</h2>
          <select
            value={form.driver_uuid ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, driver_uuid: e.target.value || null }))
            }
            className={styles.select}
          >
            <option value="">— No driver assigned —</option>
            {drivers.map((d) => (
              <option key={d.uuid} value={d.uuid}>
                {d.name}
              </option>
            ))}
          </select>
        </Card>

        {isImmediate ? null : (
          <Card>
            <h2 className={styles.sectionHeading}>Delivery</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Delivery Date</span>
                <input
                  type="date"
                  value={form.delivery_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, delivery_date: e.target.value }))}
                />
              </label>
              <label className={styles.field}>
                <span>Delivery Time</span>
                <input
                  type="text"
                  placeholder="e.g. 09:00, AM, anytime"
                  value={form.delivery_time}
                  onChange={(e) => setForm((prev) => ({ ...prev, delivery_time: e.target.value }))}
                />
              </label>
            </div>
          </Card>
        )}

        <Card>
          <h2 className={styles.sectionHeading}>Notes</h2>
          <textarea
            className={styles.textarea}
            rows={4}
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Any notes about this job…"
          />
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Attachments</h2>
          <p className={styles.deferralBanner}>
            Save the job first — files can be added on the next page once the
            job has a number to attach them to.
          </p>
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/skip/jobs')}
            disabled={createJob.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={createJob.isPending}>
            {createJob.isPending ? 'Creating…' : 'Create Job'}
          </Button>
        </div>
      </form>
    </div>
  );
}
